/**
 * pages/JobAdmin.tsx — the operations on an existing update that are not edits to its draft.
 *
 * Three things live here rather than in the editor's form, and each for the same reason: **they take effect
 * when pressed, not when Save is pressed.**
 *
 *   * **Enabling a destination is a fresh publishing decision.** The server re-asks the grant and the
 *     business unit's own allowlist at the moment it is enabled, and it can refuse — or find that another
 *     update has claimed the page in the meantime, which is a 409 and a correct outcome. A draft field that
 *     silently accumulated "enabled: true" until Save would either lie about having worked or fail long
 *     after the choice was made.
 *   * **Reassigning the owner changes who the update runs AS.** The scheduler acts as the job's owner and
 *     re-checks that person's grant at delivery, so this is the control that repairs an orphaned update —
 *     one whose owner has been suspended or deleted, which the dashboard flags and which nothing else in
 *     the interface could fix. It needs `bu_admin`, not `job_author`: choosing whose authority an update
 *     borrows is strictly more than authoring one.
 *   * **Deletion is not an edit.** It states its blast radius before it happens, because an update is the
 *     only thing standing between a Confluence page and going stale, and the run history goes with it.
 *
 * The candidate list for reassignment is the roster, which belongs to the `users` section — so an author
 * who is not also a user-administrator sees the field as a free-text account id rather than a picker. That
 * is a degradation, not a refusal: the server re-resolves whoever is named through the one resolver and
 * refuses anybody who could not reach the business unit in their own right.
 */
import { useState } from "react";
import { ApiError, api, type AdminUser } from "../lib/api";
import { humanise } from "../lib/format";
import { Badge, Banner, Button, Card, Modal, Select, TextField, useToast } from "../components/ui";
import type { JobTargetView } from "./Dashboard";

export interface JobOwner {
  readonly id: string;
  readonly email: string | null;
  readonly status: string | null;
  readonly orphaned: boolean;
}

export interface JobAdminPorts {
  readonly setTargetEnabled: (jobId: string, targetId: string, enabled: boolean) => Promise<void>;
  readonly reassign: (jobId: string, userId: string) => Promise<void>;
  readonly remove: (jobId: string) => Promise<void>;
  readonly loadCandidates: (signal: AbortSignal) => Promise<readonly AdminUser[]>;
}

export const jobAdminPorts: JobAdminPorts = {
  setTargetEnabled: async (jobId, targetId, enabled) => {
    await api.patch(
      `/api/jobs/${encodeURIComponent(jobId)}/targets/${encodeURIComponent(targetId)}`,
      { enabled },
    );
  },
  reassign: async (jobId, userId) => {
    await api.post(`/api/jobs/${encodeURIComponent(jobId)}/owner`, { userId });
  },
  remove: async (jobId) => {
    await api.delete(`/api/jobs/${encodeURIComponent(jobId)}`);
  },
  loadCandidates: async (signal) => {
    const response = await api.get<{ readonly users: readonly AdminUser[] }>("/api/admin/users", { signal });
    return response.users;
  },
};

export interface JobAdminProps {
  readonly jobId: string;
  readonly jobName: string;
  readonly owner: JobOwner;
  readonly targets: readonly JobTargetView[];
  readonly runCount?: number;
  /** `bu_admin` in this business unit. Reassignment is the only control here that needs more than authoring. */
  readonly canReassign: boolean;
  readonly candidates?: readonly AdminUser[];
  readonly onChanged: () => void;
  readonly onDeleted?: () => void;
  readonly ports?: JobAdminPorts;
}

export function JobAdmin({
  jobId,
  jobName,
  owner,
  targets,
  runCount,
  canReassign,
  candidates = [],
  onChanged,
  onDeleted,
  ports = jobAdminPorts,
}: JobAdminProps): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newOwner, setNewOwner] = useState("");

  const guard = (label: string, work: () => Promise<void>, after?: () => void): void => {
    setBusy(true);
    work()
      .then(() => {
        toast.show(label, { tone: "success" });
        (after ?? onChanged)();
      })
      .catch((cause: unknown) => {
        const failure =
          cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        toast.show(`${label} did not happen`, { tone: "danger", detail: failure.userMessage });
        // Re-read either way: a refusal here is often a fact about the world (a page claimed by another
        // update, a revoked grant) and the screen should show the world rather than the attempt.
        onChanged();
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Card title="Destinations in use">
        {targets.length === 0 ? (
          <p className="field__hint">
            This update has no destinations, so it publishes nothing however often it runs.
          </p>
        ) : (
          targets.map((target) => (
            <div key={target.id} className="row">
              <Badge tone={target.enabled ? "success" : "neutral"}>
                {target.kind === "confluence"
                  ? `Confluence ${target.spaceKey ?? "?"} / ${target.pageTitle ?? "?"}`
                  : `Slack ${target.channelId ?? "?"}`}
              </Badge>
              {target.loadBearing ? (
                <Badge tone="info" >Load-bearing</Badge>
              ) : null}
              <Button
                variant={target.enabled ? "secondary" : "primary"}
                size="sm"
                disabled={busy}
                title={
                  target.enabled
                    ? "Stop publishing here. The destination is kept, so it can be switched back on."
                    : "Publishing here is re-checked against the grant and this business unit's allowlist as it is enabled, so this can be refused."
                }
                onClick={() =>
                  guard(
                    target.enabled ? "Destination paused" : "Destination enabled",
                    () => ports.setTargetEnabled(jobId, target.id, !target.enabled),
                  )
                }
              >
                {target.enabled ? "Pause" : "Enable"}
              </Button>
            </div>
          ))
        )}
        <p className="field__hint">
          A <strong>load-bearing</strong> destination is one whose failure stops the watermark advancing, so
          the next run covers the same commits again rather than skipping past them.
        </p>
      </Card>

      <Card title="Who this update runs as">
        {owner.orphaned ? (
          <Banner tone="danger">
            {owner.email === null
              ? "The account this update runs as no longer exists."
              : `${owner.email} is ${humanise(owner.status ?? "unavailable")}, so this update cannot publish.`}{" "}
            A scheduled update acts with its owner&rsquo;s permissions and re-checks them at delivery, so it
            will keep failing until somebody with the business-unit admin role reassigns it.
          </Banner>
        ) : (
          <p className="field__hint">
            Runs as <strong>{owner.email ?? owner.id}</strong>. The scheduler acts with this
            person&rsquo;s permissions and re-checks them when it publishes — never with a system identity.
          </p>
        )}

        {canReassign ? (
          <div className="row">
            {candidates.length > 0 ? (
              <Select
                label="Reassign to"
                value={newOwner}
                options={[
                  { value: "", label: "Choose a person…" },
                  ...candidates
                    .filter((user) => user.status === "active" && user.id !== owner.id)
                    .map((user) => ({ value: user.id, label: user.email })),
                ]}
                onChange={setNewOwner}
                disabled={busy}
                hint="They must be able to reach this business unit in their own right; the server re-resolves them."
              />
            ) : (
              <TextField
                label="Reassign to (account id)"
                value={newOwner}
                onChange={setNewOwner}
                hint="The roster belongs to the people section, which you do not hold — so this takes an account id."
              />
            )}
            <Button
              variant="primary"
              disabled={busy || newOwner.trim() === ""}
              onClick={() =>
                guard("Owner reassigned", () => ports.reassign(jobId, newOwner.trim()), () => {
                  setNewOwner("");
                  onChanged();
                })
              }
            >
              Reassign
            </Button>
          </div>
        ) : (
          <p className="field__hint">
            Reassigning needs the business-unit admin role. Authoring an update and choosing whose authority
            it borrows are deliberately different permissions.
          </p>
        )}
      </Card>

      <Card title="Delete this update">
        <p className="field__hint">
          Deleting stops the schedule and removes the update&rsquo;s history. The Confluence page it
          maintains is left exactly as it last published — it is not tidied up, and it will simply stop
          changing.
        </p>
        <Button variant="danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
          Delete update
        </Button>
      </Card>

      <Modal
        open={confirmDelete}
        role="alertdialog"
        title={`Delete “${jobName}”`}
        onClose={() => setConfirmDelete(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                setConfirmDelete(false);
                guard("Update deleted", () => ports.remove(jobId), onDeleted ?? onChanged);
              }}
            >
              Delete it
            </Button>
          </>
        }
      >
        {/* The blast radius, before the act. */}
        <p>
          {targets.length === 0
            ? "This update publishes nowhere, so nothing stops being maintained."
            : `${targets.length} destination${targets.length === 1 ? "" : "s"} stop being maintained: ${targets
                .map((target) =>
                  target.kind === "confluence"
                    ? `${target.spaceKey ?? "?"} / ${target.pageTitle ?? "?"}`
                    : `Slack ${target.channelId ?? "?"}`,
                )
                .join(", ")}. Each is left as it last published.`}
          {runCount === undefined
            ? " Its run history is deleted with it."
            : ` Its ${runCount} recorded run${runCount === 1 ? "" : "s"} — what it reviewed, dropped and published — are deleted with it.`}
        </p>
        <p className="field__hint">
          This cannot be undone. The audit trail keeps the record that it was deleted, and by whom.
        </p>
      </Modal>
    </>
  );
}
